import MapLocationSelect from "@/components/mapping/MapLocationSelect"
import type { LatLng } from "@/models"
import type { IncidentLocation } from "@/models/IncidentLocation"
import { css } from "vite-css-in-js"
import { Teleport, computed, shallowRef } from "vue"
import { defineComponent, inferProps, type ReactiveComponent } from "vue-utils"
import AdditionalLocationsRow from "./AdditionalLocationsRow"
import { generateMarkerFilter } from "./generateMarkerFilter"

interface Props {
	locations: IncidentLocation[]
}

const tableStyles = css`
	grid-template-columns: max-content minmax(1em, 1fr) minmax(1em, 1fr) minmax(8rem, max-content) max-content;

	td,
	th {
		padding: 0.35rem 0.5rem;
		display: flex;
		align-items: center;
		justify-content: center;
		background-color: white;
	}

	td {
		display: flex;
		column-gap: 0.25rem;
		align-items: center;

		font-size: 1rem;
	}

	td:first-child {
		display: flex;
		gap: 0.5em;
	}

	td:last-child,
	th:last-child {
		border: none !important;
		background-color: transparent !important;
	}

	td:last-child {
		padding: 0 0.35rem;

		button {
			padding: 0.25rem 0.5rem;
		}
	}

	tbody > tr:nth-child(2n + 1) > td {
		background-color: #f0f0f0;
	}
`

const AdditionalLocationsTable: ReactiveComponent<Props> = (props) => {
	const locationShown = shallowRef<LatLng | null>(null)
	const currentIndex = shallowRef<number | null>(null)
	const currentFilter = shallowRef<string | null>(null)

	const sortedLocations = computed(() => [...props.locations].sort((l1, l2) => l1.receivedAt.compareTo(l2.receivedAt)))

	return () => (
		<>
			{props.locations.length === 0 ? (
				<p class="my-0">No Additional Incident Locations have been added.</p>
			) : (
				<table class={["grid-table", tableStyles]}>
					<thead>
						<tr>
							<th>No.</th>
							<th>Latitude</th>
							<th>Longitude</th>
							<th>Received At</th>
							<th />
						</tr>
					</thead>
					<tbody>
						{sortedLocations.value.map((location, index) => (
							<AdditionalLocationsRow
								key={location.id}
								index={index}
								location={location}
								openOnMap={() => {
									locationShown.value = location.position
									currentIndex.value = index
									currentFilter.value = generateMarkerFilter(index + 1)
								}}
							/>
						))}
					</tbody>

					{locationShown.value && (
						<Teleport to="body">
							<MapLocationSelect
								showLatLngInputs={false}
								locationName={`Additional Incident Location${currentIndex.value !== null ? ` ${currentIndex.value}` : ""}`}
								location={locationShown.value}
								markerFilter={currentFilter.value ?? undefined}
								setLocation={() => void 0}
								cancel={() => (locationShown.value = null)}
								readOnly
							/>
						</Teleport>
					)}
				</table>
			)}
		</>
	)
}

export default defineComponent(AdditionalLocationsTable, inferProps<Props>())
